import type { FleetMessageAudiences } from "@/models/repositories/Driver";
import type { FleetMessageRecipientMode } from "@/types/fleetMessage";

export type FleetMessageRecipientOption = { value: FleetMessageRecipientMode; label: string };

/**
 * The feed ids a recipient option addresses.
 *
 * `table` carries none of its own — the sender is on their way back to the driver table to build the set — so the
 * pane's Send stays disabled until they return with rows checked.
 */
export function recipientsForMode(
  mode: FleetMessageRecipientMode | null,
  audiences: FleetMessageAudiences,
  tableSelection: string[],
): string[] {
  switch (mode) {
    case "selection":
      return tableSelection;
    case "all":
      return audiences.all;
    case "mine":
      return audiences.mine;
    default:
      return [];
  }
}

/**
 * The recipient option a freshly opened pane starts on.
 *
 * Rows checked in the driver table are the sender's own most recent statement of who they meant, so they win; with
 * nothing checked the pane starts on whatever option the sender left it on, and on none at all the first time.
 */
export function initialRecipientMode(
  tableSelection: string[],
  storedMode: FleetMessageRecipientMode | null,
): FleetMessageRecipientMode | null {
  if (tableSelection.length > 0) return "selection";
  return storedMode === "selection" ? null : storedMode;
}

/** Whether two feed-id lists name the same channels in the same order. */
export function sameRecipients(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/**
 * The recipient options the pane offers, in order.
 *
 * With rows already checked in the driver table, the Edit selection button is the way back to that table, so the
 * table option is left out rather than offering a second route to the same place. With nothing checked it leads,
 * because building a set on the table is the replacement for the group tree that used to sit here.
 */
export function recipientOptions(
  audiences: FleetMessageAudiences,
  tableSelection: string[],
): FleetMessageRecipientOption[] {
  const audienceOptions: FleetMessageRecipientOption[] = [
    { value: "all", label: `All Drivers (${audiences.all.length})` },
    { value: "mine", label: `My Drivers (${audiences.mine.length})` },
  ];
  if (tableSelection.length > 0) return audienceOptions;
  return [{ value: "table", label: "Choose drivers from the table" }, ...audienceOptions];
}
